// src/modules/units/tyres/views/suppliers.api.js
//
// Supplier resource client for Tyre Management. Suppliers are scoped by the
// backend to the ACTIVE customer, so customerId is deliberately never sent from
// this client. POST/PATCH/DELETE may return 403 for non-administrators; callers
// surface that message rather than trying to infer permissions locally.

import { getUserData } from '../../../auth/views/login/auth.api.js';
import { emitTyreEvent, TYRE_EVENTS } from '../shared/tyre-events.js';

const baseUrl = 'https://dashboard.pressuretrack.co.za/api';

// The server's own reason, so a rejected save says WHAT was wrong. ASP.NET model
// validation answers { title, errors: { Field: ["msg", …] } } rather than { message }.
function failureMessage(data) {
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  const errs = data?.errors && typeof data.errors === 'object'
    ? Object.entries(data.errors).flatMap(([k, v]) => (Array.isArray(v) ? v : [v]).map((m) => `${k}: ${m}`))
    : [];
  if (errs.length) return errs.join(' ');
  return data?.title || 'Supplier request failed.';
}

async function request(path, options = {}) {
  const { token } = getUserData() || {};

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options.headers || {})
    },
    credentials: 'include'
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    return {
      success: false,
      status: response.status,
      message: failureMessage(data),
      data
    };
  }

  return { success: true, status: response.status, data };
}

const PAGE_SIZE = 100;
const MAX_PAGES = 50;

function supplierQuery({ sort = 'supplierName', limit, offset = 0 } = {}) {
  const q = new URLSearchParams();
  if (sort) q.set('sort', sort);
  if (Number(limit) > 0) q.set('limit', String(limit));
  if (Number(offset) >= 0) q.set('offset', String(offset));
  return q;
}

/** List suppliers for the active customer. Without an explicit positive limit,
 * page until all suppliers have been collected so dropdowns are never silently
 * truncated at the endpoint's default 20 rows. */
export async function fetchSuppliers(opts = {}, { signal } = {}) {
  if (Number(opts.limit) > 0) {
    return request(`/supplier?${supplierQuery(opts).toString()}`, { method: 'GET', signal });
  }

  const rows = [];
  let count = NaN;
  let status = 200;

  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await request(
      `/supplier?${supplierQuery({ ...opts, limit: PAGE_SIZE, offset: rows.length }).toString()}`,
      { method: 'GET', signal }
    );
    if (!res.success) return res;

    status = res.status;
    const batch = Array.isArray(res.data?.results) ? res.data.results : [];
    count = Number(res.data?.count);
    rows.push(...batch);

    if (batch.length < PAGE_SIZE) break;
    if (Number.isFinite(count) && rows.length >= count) break;
  }

  rows.sort((a, b) =>
    String(a?.supplierName ?? '').localeCompare(
      String(b?.supplierName ?? ''),
      undefined,
      { numeric: true, sensitivity: 'base' }
    )
  );

  return {
    success: true,
    status,
    data: {
      count: Number.isFinite(count) ? count : rows.length,
      results: rows
    }
  };
}

export async function fetchSupplier(supplierId) {
  return request(`/supplier/${encodeURIComponent(supplierId)}`, { method: 'GET' });
}

export async function createSupplier(payload = {}) {
  const body = {
    supplierName: payload.supplierName,
    contactName: payload.contactName || undefined,
    eMailAddress: payload.eMailAddress || undefined,
    contactNumber: payload.contactNumber || undefined
  };

  const res = await request('/supplier', {
    method: 'POST',
    body: JSON.stringify(body)
  });

  if (res.success) {
    emitTyreEvent(TYRE_EVENTS.SUPPLIERS_CHANGED, {
      reason: 'create',
      supplierId: res.data?.supplierId
    });
  }

  return res;
}

export async function updateSupplier(supplierId, payload = {}) {
  const body = {
    supplierName: payload.supplierName,
    contactName: payload.contactName || undefined,
    eMailAddress: payload.eMailAddress || undefined,
    contactNumber: payload.contactNumber || undefined
  };

  const res = await request(`/supplier/${encodeURIComponent(supplierId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });

  if (res.success) {
    emitTyreEvent(TYRE_EVENTS.SUPPLIERS_CHANGED, { reason: 'update', supplierId });
  }

  return res;
}

export async function deleteSupplier(supplierId) {
  const res = await request(`/supplier/${encodeURIComponent(supplierId)}`, {
    method: 'DELETE'
  });

  if (res.success) {
    emitTyreEvent(TYRE_EVENTS.SUPPLIERS_CHANGED, { reason: 'delete', supplierId });
  }

  return res;
}
